import { useMutation, useQueryClient } from '@tanstack/react-query';

import { type CppInvoiceQuery, type CppInvoiceResponse, generateCppInvoice } from '~/services/LuxLoyalty';

import { costPerPointQueryKeys } from './costPerPointQueryKeys';

/** Parameters a caller supplies; `mode` is fixed by the hook. */
type RefreshQuery = Omit<CppInvoiceQuery, 'mode'>;

/**
 * Recomputes every per-transaction cost in a period and overwrites what was stored.
 *
 * A mutation rather than a query because it deliberately discards and rewrites persisted data:
 * viewing an invoice will happily serve a cached period back, which is wrong once the underlying
 * data moves — a backdated transaction landing in a tiered period pushes every later transaction
 * into a different band, so they all have to be priced again.
 *
 * On success the recomputed invoice is written straight into the preview cache so the document
 * updates without a second round trip, and every other invoicing query is invalidated: the
 * transaction-level costs the Transactions tab renders have just changed too.
 */
export function useRefreshCppInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (query: RefreshQuery): Promise<CppInvoiceResponse> => {
      return await generateCppInvoice({ ...query, mode: 'refresh' });
    },
    onSuccess: (response, query) => {
      const previewKey = costPerPointQueryKeys.invoice({ ...query, mode: 'preview' });
      queryClient.setQueryData(previewKey, response);

      // `invoices()` is a prefix of `previewKey`, so invalidating the whole subtree would mark the
      // response just written as stale and immediately refetch it — a second POST that recomputes
      // what is already in hand. Everything else under the subtree (the paginated transaction
      // pages, whose per-row costs this refresh rewrote) does need invalidating.
      queryClient.invalidateQueries({
        queryKey: costPerPointQueryKeys.invoices(),
        predicate: ({ queryKey }) => JSON.stringify(queryKey) !== JSON.stringify(previewKey),
      });
    },
  });
}
